import React, { useState } from 'react';
import { API_BASE } from './lib/runtimeConfig';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Parsea el campo images del DB (puede ser JSON array o CSV) y devuelve URLs absolutas
const parseImages = (raw) => {
    if (!raw || raw === '' || raw === '[]') return [];
    return raw
        .replace(/[\[\]"']/g, '')
        .split(',')
        .map(s => {
            const p = s.trim();
            if (!p) return null;
            if (p.startsWith('http')) return p;
            const filename = p.replace(/^\/?(?:uploads\/)?/, '');
            return `${API_BASE}/uploads/${encodeURIComponent(filename)}`;
        })
        .filter(Boolean);
};

const PropertyGallery = ({ property }) => {
    const images = parseImages(property.images);
    const [lightbox, setLightbox] = useState(null);

    const openLightbox = (index) => { if (images[index]) setLightbox(index); };
    const closeLightbox = () => setLightbox(null);
    const prevImg = () => setLightbox(i => (i > 0 ? i - 1 : images.length - 1));
    const nextImg = () => setLightbox(i => (i < images.length - 1 ? i + 1 : 0));

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowLeft') prevImg();
        if (e.key === 'ArrowRight') nextImg();
        if (e.key === 'Escape') closeLightbox();
    };

    const hoverIn = (e) => { e.currentTarget.style.filter = 'brightness(0.85)'; e.currentTarget.style.transform = 'scale(1.03)'; };
    const hoverOut = (e) => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'scale(1)'; };

    if (!images.length) return null;

    return (
        <div style={galleryContainer} className="property-gallery-container">
            <div style={galleryGrid} className="property-gallery-grid">

                {/* IMAGEN PRINCIPAL */}
                <div style={mainImageWrapper} className="property-gallery-main" onClick={() => openLightbox(0)}>
                    <img
                        src={images[0]}
                        alt="Vista principal"
                        style={imgStyle}
                        onMouseEnter={hoverIn}
                        onMouseLeave={hoverOut}
                        onError={e => { e.target.style.display = 'none'; }}
                    />
                </div>

                {/* CUADRÍCULA LATERAL (2×2) */}
                <div style={sideGrid} className="property-gallery-side">
                    {[1, 2, 3, 4].map(index => (
                        <div key={index} style={sideImageWrapper} onClick={() => openLightbox(index)}>
                            {images[index] ? (
                                <img
                                    src={images[index]}
                                    alt={`Vista ${index}`}
                                    style={imgStyle}
                                    onMouseEnter={hoverIn}
                                    onMouseLeave={hoverOut}
                                    onError={e => { e.target.style.display = 'none'; }}
                                />
                            ) : (
                                <div style={placeholderStyle} />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* BOTÓN VER TODAS */}
            {images.length > 5 && (
                <button style={showMoreBtn} onClick={() => openLightbox(0)}>
                    Ver las {images.length} fotos
                </button>
            )}

            {/* LIGHTBOX */}
            {lightbox !== null && (
                <div
                    style={overlay}
                    onClick={closeLightbox}
                    onKeyDown={handleKeyDown}
                    tabIndex={0}
                    autoFocus
                >
                    <button style={closeBtn} onClick={closeLightbox} aria-label="Cerrar">
                        <X size={24} color="white" />
                    </button>

                    <div style={counter}>{lightbox + 1} / {images.length}</div>

                    {images.length > 1 && (
                        <button style={navBtnLeft} onClick={e => { e.stopPropagation(); prevImg(); }}>
                            <ChevronLeft size={32} color="white" />
                        </button>
                    )}

                    <img
                        src={images[lightbox]}
                        alt={`Foto ${lightbox + 1}`}
                        style={lightboxImg}
                        onClick={e => e.stopPropagation()}
                        onError={e => { e.target.alt = 'Imagen no disponible'; }}
                    />

                    {images.length > 1 && (
                        <button style={navBtnRight} onClick={e => { e.stopPropagation(); nextImg(); }}>
                            <ChevronRight size={32} color="white" />
                        </button>
                    )}

                    {images.length > 1 && (
                        <div style={thumbsRow} onClick={e => e.stopPropagation()}>
                            {images.map((img, i) => (
                                <div
                                    key={i}
                                    onClick={() => setLightbox(i)}
                                    style={{ ...thumbWrapper, outline: i === lightbox ? '3px solid #fff' : '2px solid rgba(255,255,255,0.3)' }}
                                >
                                    <img src={img} alt="" style={thumbImg} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// --- ESTILOS ---
const galleryContainer = { position: 'relative', marginBottom: '32px', marginTop: '20px' };

const galleryGrid = {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
    height: '480px', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#f0f0f0'
};

const mainImageWrapper = { width: '100%', height: '100%', overflow: 'hidden', cursor: 'pointer', borderRight: '2px solid #fff' };
const sideGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '10px' };
const sideImageWrapper = { width: '100%', height: '100%', overflow: 'hidden', cursor: 'pointer', background: '#e2e8f0' };

const imgStyle = { width: '100%', height: '100%', objectFit: 'cover', transition: 'all 0.4s ease-in-out', display: 'block' };
const placeholderStyle = { width: '100%', height: '100%', background: '#e8edf2' };

const showMoreBtn = {
    position: 'absolute', bottom: '20px', right: '20px', backgroundColor: '#fff', color: '#222',
    padding: '10px 18px', borderRadius: '10px', border: '1px solid #222', fontSize: '14px',
    fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10
};

const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.93)', zIndex: 9999,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', outline: 'none'
};
const closeBtn = {
    position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '50%', width: 44, height: 44,
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10001
};
const counter = {
    position: 'absolute', top: 24, left: '50%', transform: 'translateX(-50%)',
    color: 'rgba(255,255,255,0.8)', fontSize: 14, fontWeight: 700, letterSpacing: 1
};
const navBtnLeft = {
    position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '50%', width: 52, height: 52, display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', zIndex: 10001
};
const navBtnRight = {
    position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '50%', width: 52, height: 52, display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', zIndex: 10001
};
const lightboxImg = {
    maxWidth: '90vw', maxHeight: '70vh', objectFit: 'contain', borderRadius: 12,
    boxShadow: '0 16px 48px rgba(0,0,0,0.5)'
};
const thumbsRow = {
    position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
    display: 'flex', gap: 8, overflowX: 'auto', maxWidth: '90vw', padding: '4px'
};
const thumbWrapper = { width: 60, height: 44, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', flexShrink: 0 };
const thumbImg = { width: '100%', height: '100%', objectFit: 'cover' };

export default PropertyGallery;
